// Environment banner — distinguishes the staging/preview build from the
// live production site at https://yahwav.com so contributors and admins
// never confuse one with the other.
//
// Detection is host-based (not env-var-based) so the banner ALWAYS reflects
// where the user actually is, even if a build accidentally ships with the
// wrong env vars baked in. Production hostnames must match yahwav.com or
// www.yahwav.com — anything else is treated as preview/test.

import React, { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck } from "lucide-react";

const PROD_HOSTS = new Set(["yahwav.com", "www.yahwav.com"]);

function detectEnv() {
  if (typeof window === "undefined") return "preview";
  const host = (window.location.hostname || "").toLowerCase();
  return PROD_HOSTS.has(host) ? "production" : "preview";
}

export default function EnvBanner() {
  // Read once on mount — the host can't change without a reload.
  const [env] = useState(detectEnv);

  // Tag <body> so any component (or a piece of CSS) can react to the env
  // without needing to import this module.
  useEffect(() => {
    document.body.setAttribute("data-env", env);
  }, [env]);

  if (env === "production") {
    // Production: tiny, low-key tag in the corner. Confirmation, not warning.
    return (
      <div
        data-testid="env-banner-production"
        data-env="production"
        className="fixed bottom-3 right-3 z-[60] pointer-events-none"
        aria-label="Live production"
      >
        <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1 backdrop-blur-sm">
          <ShieldCheck className="w-3 h-3 text-emerald-300" strokeWidth={2} />
          <span className="overline text-[9px] text-emerald-300 tracking-[0.18em]">LIVE PRODUCTION</span>
        </div>
      </div>
    );
  }

  // Preview: full-width sticky bar, impossible to miss on mobile and desktop.
  return (
    <div
      data-testid="env-banner-preview"
      data-env="preview"
      role="status"
      className="sticky top-0 z-[70] w-full border-b border-amber-500/40 bg-amber-500/10 backdrop-blur"
    >
      <div className="mx-auto max-w-7xl px-3 sm:px-6 py-1.5 flex items-center gap-2 sm:gap-3 text-amber-200">
        <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" strokeWidth={2} />
        <div className="text-[10px] sm:text-xs font-bold tracking-[0.18em] uppercase whitespace-nowrap">
          Preview / Test
        </div>
        <div className="hidden sm:block w-px h-3.5 bg-amber-500/30" />
        <div className="text-[10px] sm:text-xs text-amber-100/80 truncate">
          Uploads here do <span className="font-bold text-amber-100">NOT</span> appear on live Yah-Wave.
        </div>
        <a
          href="https://yahwav.com"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto overline text-[9px] sm:text-[10px] tracking-[0.18em] text-amber-100 hover:text-white border border-amber-500/40 hover:border-amber-400 rounded-full px-2 py-0.5 whitespace-nowrap"
          data-testid="env-banner-prod-link"
        >
          Go live →
        </a>
      </div>
    </div>
  );
}
